import type {
  ButtonAnimation,
  DiscountType,
  OfferDesign,
  OfferProduct,
  OfferTemplate,
  QuantityOfferConfig,
  QuantityOfferTier,
} from '@cod/config-schema';
import { BEST_DEAL_TEXT_MAX, buttonAnimation, VERTICAL_MAX_TIERS } from '@cod/config-schema';
import {
  Box,
  Button,
  Card,
  IconButton,
  Input,
  Label,
  Text,
  Thumbnail,
  Title,
  useToast,
} from '@nimbus-ds/components';
import {
  ArrowLeftIcon,
  CheckIcon,
  InfoCircleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@nimbus-ds/icons';
import { Layout, Page } from '@nimbus-ds/patterns';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferEditorSkeleton } from '../components/skeletons.js';
import {
  CheckboxControl,
  ColorControl,
  ControlledToggle,
  NumberControl,
  SelectControl,
  SliderControl,
  TextControl,
} from '../components/controls.js';
import { ProductPicker } from '../components/ProductPicker.js';
import { LivePreview } from '../designer/LivePreview.js';
import { useAppState } from '../state/AppState.js';
import { useNavigationGuard, useUnsavedChanges } from '../state/NavigationGuard.js';
import { applyPreselect } from './quantityOffers.model.js';

const TITLE_MAX = 185;
const DEFAULT_PRICE_COLOR = '#000000';
const DEFAULT_PLAQUE_TEXT_COLOR = '#ffffff';
const DEFAULT_PLAQUE_BG_COLOR = '#0074bf';

/** A tiny mock illustration of how each template arranges its offer cards, drawn from
 * simple bars so the template chooser reads at a glance (no real data needed). */
const MOCK_SHAPE: Record<OfferTemplate, string> = {
  classic: '#9ec5fe',
  modern: '#a7e3c0',
  vertical: '#c9bdf2',
};
function TemplateMock({ template }: { template: OfferTemplate }): ReactElement {
  const shape = MOCK_SHAPE[template];
  if (template === 'modern') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ height: 8, borderRadius: 4, background: shape }} />
        ))}
      </div>
    );
  }
  if (template === 'vertical') {
    return (
      <div style={{ display: 'flex', gap: 6, height: 34 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1, borderRadius: 4, background: shape }} />
        ))}
      </div>
    );
  }
  // classic: stacked rows of a thumbnail dot + a text bar
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1].map((i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: shape,
              flex: '0 0 auto',
            }}
          />
          <div style={{ height: 8, borderRadius: 4, background: shape, flex: 1 }} />
        </div>
      ))}
    </div>
  );
}

/** Quick-pick color palettes for the offer cards. Each swatch shows the soft background on
 * its left half and the hard border color on its right half. Applying one fills the
 * background and border colors; the chosen id is stored so the editor can highlight the
 * active swatch. */
/** How many swatches fit the strip before it scrolls, and the gap between them (px). The
 * swatches share the strip's width in these terms, so the count holds at any card width. */
const PALETTES_PER_VIEW = 5;
const PALETTE_GAP = 8;
const PALETTES: { id: string; bg: string; border: string }[] = [
  { id: 'blue', bg: 'rgba(217,235,246,1)', border: 'rgba(0,116,191,1)' },
  { id: 'green', bg: 'rgba(223,240,216,1)', border: 'rgba(60,118,61,1)' },
  { id: 'purple', bg: 'rgba(235,225,245,1)', border: 'rgba(123,63,184,1)' },
  { id: 'orange', bg: 'rgba(252,238,213,1)', border: 'rgba(224,142,30,1)' },
  { id: 'red', bg: 'rgba(248,215,218,1)', border: 'rgba(211,47,47,1)' },
  { id: 'dark', bg: 'rgba(226,229,233,1)', border: 'rgba(33,37,41,1)' },
  { id: 'teal', bg: 'rgba(214,240,237,1)', border: 'rgba(0,150,136,1)' },
  { id: 'pink', bg: 'rgba(252,228,236,1)', border: 'rgba(233,30,99,1)' },
  { id: 'yellow', bg: 'rgba(255,249,196,1)', border: 'rgba(251,192,45,1)' },
  { id: 'indigo', bg: 'rgba(226,227,247,1)', border: 'rgba(63,81,181,1)' },
  { id: 'lime', bg: 'rgba(240,244,195,1)', border: 'rgba(175,180,43,1)' },
  { id: 'cyan', bg: 'rgba(209,237,242,1)', border: 'rgba(0,151,167,1)' },
  { id: 'brown', bg: 'rgba(239,228,219,1)', border: 'rgba(121,85,72,1)' },
  { id: 'magenta', bg: 'rgba(243,224,247,1)', border: 'rgba(156,39,176,1)' },
  { id: 'gray', bg: 'rgba(236,239,241,1)', border: 'rgba(96,125,139,1)' },
  { id: 'slate', bg: 'rgba(226,232,240,1)', border: 'rgba(71,85,105,1)' },
];

/**
 * Sales Booster → Quantity Offers editor. Edits a LOCAL draft of one offer (seeded from
 * `config._future.quantity_offers`); the live preview reads the draft directly so every
 * keystroke updates instantly with no network round-trip. The draft is persisted via the
 * shared config save when the merchant toggles the on/off pill or navigates back (there is
 * no separate Save button in the design).
 */
export function QuantityOfferEditor({
  offerId,
  isNew,
  onBack,
}: {
  offerId: string;
  isNew: boolean;
  onBack: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { config, setConfig, saveQuantityOffers } = useAppState();
  const { addToast } = useToast();
  const guard = useNavigationGuard();
  const [draft, setDraft] = useState<QuantityOfferConfig | null>(
    () => config?._future.quantity_offers.find((o) => o.id === offerId) ?? null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  // Becomes true on the first edit so the sticky Save button appears, and back to false
  // once persisted. The offer is saved only when the merchant clicks Save.
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // A new offer is seeded into the config locally so this editor can load it, but it isn't
  // written to the server until the first successful save. Until then, going back must drop
  // it so an incomplete offer never lingers. Existing offers start already persisted.
  const [persisted, setPersisted] = useState(!isNew);
  // The header is transparent (matching the page) until it actually sticks to the top on
  // scroll, at which point it gets a solid background + shadow so content doesn't show
  // through. A zero-height sentinel above it flips `stuck` when it scrolls out of view.
  const [stuck, setStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry?.isIntersecting), {
      threshold: [1],
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Drop a new offer that was never saved. It exists in the config only because this editor
  // loads from there; nothing on the server knows about it.
  const dropUnpersisted = (): void =>
    setConfig((prev) => ({
      ...prev,
      _future: {
        ...prev._future,
        quantity_offers: prev._future.quantity_offers.filter((o) => o.id !== offerId),
      },
    }));

  /**
   * What "Discard changes" means in this editor, which has no Discard button of its own: an
   * existing offer goes back to the version the server has, and a brand-new one is dropped
   * entirely — reverting it would leave a blank offer sitting in the list.
   *
   * Registered above the `!config` bail-out below so the hook order never changes.
   */
  useUnsavedChanges(dirty, () => {
    if (persisted) {
      const savedOffer = config?._future.quantity_offers.find((o) => o.id === offerId);
      if (savedOffer) setDraft(savedOffer);
    } else {
      dropUnpersisted();
    }
    setDirty(false);
  });

  // Defensive: the shell already gates on `status`, so this only fires if the config is
  // momentarily absent. The skeleton is the editor's own shape — sticky header, two columns.
  if (!config || !draft) return <OfferEditorSkeleton />;

  const update = (patch: Partial<QuantityOfferConfig>): void => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };

  const updateTier = (id: string, patch: Partial<QuantityOfferTier>): void => {
    setDraft((d) =>
      d
        ? { ...d, tiers: d.tiers.map((tier) => (tier.id === id ? { ...tier, ...patch } : tier)) }
        : d,
    );
    setDirty(true);
  };

  // Preselection is exclusive — checking one offer clears the others. See `applyPreselect`.
  const setPreselect = (id: string, preselect: boolean): void => {
    setDraft((d) => (d ? { ...d, tiers: applyPreselect(d.tiers, id, preselect) } : d));
    setDirty(true);
  };

  const updateDesign = (patch: Partial<OfferDesign>): void =>
    update({ design: { ...draft.design, ...patch } });

  // 🚨 The vertical cap is enforced from BOTH directions, and neither half is optional.
  //
  // The layout only stays readable as a few side-by-side columns, so the renderer draws just the
  // first VERTICAL_MAX_TIERS cards. With vertical selected, "Add offer" therefore stops at the cap
  // (below). Past the cap, vertical is greyed out here — it used to be selectable with a toast
  // saying the extras were kept but hidden, which is a merchant configuring four offers and
  // shipping three. A warning is not a substitute for not letting it happen.
  const verticalUnavailable = draft.tiers.length > VERTICAL_MAX_TIERS;

  const selectTemplate = (template: OfferTemplate): void => {
    // Belt and braces: the card carries no click handler when it's unavailable.
    if (template === 'vertical' && verticalUnavailable) return;
    updateDesign({ template });
  };

  const toggleEnabled = (): void => update({ enabled: !draft.enabled });

  const save = async (): Promise<void> => {
    const offers = config._future.quantity_offers.some((o) => o.id === draft.id)
      ? config._future.quantity_offers.map((o) => (o.id === draft.id ? draft : o))
      : [...config._future.quantity_offers, draft];
    // Optimistic local update; saveQuantityOffers re-reads the latest server config and merges
    // only the offers slice, so a concurrent Form Designer / Settings save isn't overwritten.
    setConfig((prev) => ({ ...prev, _future: { ...prev._future, quantity_offers: offers } }));
    setSaving(true);
    const ok = await saveQuantityOffers(offers);
    setSaving(false);
    if (ok) {
      setDirty(false);
      setPersisted(true);
    }
    addToast({
      id: 'qo-editor-save',
      type: ok ? 'success' : 'danger',
      text: ok ? t('common.saved') : t('common.saveError'),
    });
  };

  // Back is a section change like any other, so it goes through the navigation guard: with
  // unsaved edits it asks first instead of throwing them away on the way out. Going back from a
  // new offer that was never saved still drops it from the config so an incomplete offer isn't
  // left behind. A saved offer (or an existing one) is untouched.
  const handleBack = (): void =>
    guard.request(() => {
      if (!persisted) dropUnpersisted();
      onBack();
    });

  const addProducts = (products: OfferProduct[]): void => update({ products });

  const removeProduct = (id: number): void =>
    update({ products: draft.products.filter((p) => p.id !== id) });

  const addTier = (): void => {
    const tier: QuantityOfferTier = {
      id: crypto.randomUUID(),
      title: t('quantityOfferEditor.offerNamePlaceholder'),
      quantity: draft.tiers.length + 1,
      preselect: false,
      hide_compare_price: false,
      discount_type: 'none',
      discount_value: 0,
      price_color: DEFAULT_PRICE_COLOR,
      plaque_text: '',
      plaque_text_color: DEFAULT_PLAQUE_TEXT_COLOR,
      plaque_bg_color: DEFAULT_PLAQUE_BG_COLOR,
      image_url: '',
    };
    setDraft((d) => (d ? { ...d, tiers: [...d.tiers, tier] } : d));
    setDirty(true);
    setEditingTierId(tier.id);
  };

  const removeTier = (id: string): void => {
    setDraft((d) => (d ? { ...d, tiers: d.tiers.filter((tier) => tier.id !== id) } : d));
    setDirty(true);
    if (editingTierId === id) setEditingTierId(null);
  };

  const discountOptions: { value: DiscountType; label: string }[] = [
    { value: 'percentage', label: t('quantityOfferEditor.tier.discountPercentage') },
    { value: 'fixed', label: t('quantityOfferEditor.tier.discountFixed') },
    { value: 'none', label: t('quantityOfferEditor.tier.discountNone') },
  ];

  const templates: { value: OfferTemplate; label: string }[] = [
    { value: 'classic', label: t('quantityOfferEditor.design.templateClassic') },
    { value: 'modern', label: t('quantityOfferEditor.design.templateModern') },
    { value: 'vertical', label: t('quantityOfferEditor.design.templateVertical') },
  ];

  const design = draft.design;
  // The vertical template caps how many offers it can display, so once that many exist the
  // "Add offer" button is disabled (a caption explains why). See `verticalUnavailable` for the
  // other half of the cap.
  const verticalOfferLimit = design.template === 'vertical';
  const verticalLimitReached = verticalOfferLimit && draft.tiers.length >= VERTICAL_MAX_TIERS;
  const editingTier = draft.tiers.find((tier) => tier.id === editingTierId) ?? null;
  // An offer isn't valid until it has a name and at least one product, so the Save button
  // stays disabled until both are provided (a caption explains what's missing).
  const canSave = draft.name.trim().length > 0 && draft.products.length > 0;

  return (
    <Page maxWidth="1200px">
      <Page.Body>
        {/* Sentinel: sits just above the sticky header so the IntersectionObserver can tell
            when the header has actually stuck to the top. */}
        <div ref={sentinelRef} style={{ height: 0 }} />
        {/* Sticky header: back arrow, title, on/off pill, and the Save button (shown once
            there are unsaved changes). Transparent until stuck, so at rest it blends into the
            page; once stuck it gets a solid background + shadow. */}
        <Box
          position="sticky"
          top="0"
          zIndex="100"
          backgroundColor={stuck ? 'neutral-background' : undefined}
          boxShadow={stuck ? '2' : undefined}
          borderRadius={stuck ? '2' : undefined}
          paddingX="4"
          paddingY="3"
          marginBottom="4"
          display="flex"
          alignItems="center"
          gap="2"
        >
          <IconButton
            size="2rem"
            title={t('quantityOfferEditor.back')}
            source={<ArrowLeftIcon />}
            onClick={handleBack}
          />
          <Title as="h3">
            {isNew ? t('quantityOfferEditor.addTitle') : t('quantityOfferEditor.editTitle')}
          </Title>
          <Box flex="1" />
          <ControlledToggle name="offer-enabled" active={draft.enabled} onChange={toggleEnabled} />
          <Text>
            {draft.enabled ? t('quantityOfferEditor.active') : t('quantityOfferEditor.inactive')}
          </Text>
          {dirty && (
            <Button
              appearance="primary"
              onClick={() => void save()}
              disabled={saving || !canSave}
              title={!canSave ? t('quantityOfferEditor.saveRequirement') : undefined}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </Box>
        {dirty && !canSave && (
          <Box marginBottom="4" paddingX="4">
            <Text fontSize="caption" color="danger-textLow">
              {t('quantityOfferEditor.saveRequirement')}
            </Text>
          </Box>
        )}

        <Layout columns="2 - symmetric">
          {/* `minWidth="0"`, like the Designer: a `1fr` track's floor is its content's min-content,
              and the live preview renders the real form at its real width — so without this the
              editor column was sized by the preview and the page scrolled sideways. */}
          <Layout.Section minWidth="0">
            <Box display="flex" flexDirection="column" gap="4">
              {/* Section 1: configure the offer */}
              <Text fontWeight="bold">{t('quantityOfferEditor.configureOffer')}</Text>
              <Card>
                <Card.Body>
                  <TextControl
                    id="offer-name"
                    label={t('quantityOfferEditor.offerName')}
                    value={draft.name}
                    onChange={(name) => update({ name })}
                  />
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <Box display="flex" flexDirection="column" gap="5">
                    <Text fontWeight="medium">{t('quantityOfferEditor.productsHeading')}</Text>
                    <Box>
                      <Button
                        className="cod-btn-black"
                        size="small"
                        onClick={() => setPickerOpen(true)}
                      >
                        {t('quantityOfferEditor.selectProducts')}
                      </Button>
                    </Box>
                    {draft.products.length > 0 && (
                      <Box display="flex" flexDirection="column" gap="3">
                        {draft.products.map((product) => (
                          <Box
                            key={product.id}
                            display="flex"
                            alignItems="center"
                            gap="3"
                            padding="2"
                            borderColor="neutral-surfaceHighlight"
                            borderStyle="solid"
                            borderWidth="1"
                            borderRadius="2"
                          >
                            <Box width="40px" flex="0 0 auto">
                              <Thumbnail
                                width="40px"
                                aspectRatio="1/1"
                                alt={product.name}
                                src={product.image ?? ''}
                              />
                            </Box>
                            <Box flex="1" minWidth="0">
                              <Text>{product.name}</Text>
                              <Text fontSize="caption" color="neutral-textLow">
                                {t('quantityOfferEditor.productId')}: {product.id}
                              </Text>
                            </Box>
                            <IconButton
                              size="2rem"
                              title={t('quantityOfferEditor.removeProduct')}
                              source={<TrashIcon />}
                              onClick={() => removeProduct(product.id)}
                              style={{ color: '#d6303a' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Card.Body>
              </Card>

              {/* Section 2: configure the quantity tiers (only once a product exists) */}
              {draft.products.length > 0 && (
                <>
                  <Text fontWeight="bold">{t('quantityOfferEditor.configureTiers')}</Text>
                  <Card>
                    <Card.Body>
                      <Box display="flex" flexDirection="column" gap="2">
                        <Box display="flex" alignItems="center" gap="2" paddingX="2">
                          <Box flex="1">
                            <Text fontWeight="medium">{t('quantityOfferEditor.tableTitle')}</Text>
                          </Box>
                          <Box width="80px">
                            <Text fontWeight="medium">
                              {t('quantityOfferEditor.tableQuantity')}
                            </Text>
                          </Box>
                          <Box width="72px" />
                        </Box>

                        {draft.tiers.map((tier, index) => (
                          <Box
                            key={tier.id}
                            display="flex"
                            alignItems="center"
                            gap="2"
                            paddingX="2"
                            paddingY="2"
                            borderColor="neutral-surfaceHighlight"
                            borderStyle="solid"
                            borderWidth="1"
                            borderRadius="2"
                          >
                            <Box flex="1" minWidth="0">
                              <Text>{tier.title}</Text>
                            </Box>
                            <Box width="80px">
                              <Text>{tier.quantity}</Text>
                            </Box>
                            <Box display="flex" gap="1" width="72px" justifyContent="flex-end">
                              <IconButton
                                size="2rem"
                                title={t('quantityOfferEditor.editTier')}
                                source={<PencilIcon />}
                                onClick={() =>
                                  setEditingTierId((cur) => (cur === tier.id ? null : tier.id))
                                }
                              />
                              {index > 0 && (
                                <IconButton
                                  size="2rem"
                                  title={t('quantityOfferEditor.deleteTier')}
                                  source={<TrashIcon />}
                                  onClick={() => removeTier(tier.id)}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}

                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap="1"
                          marginTop="2"
                        >
                          <Button onClick={addTier} disabled={verticalLimitReached}>
                            <PlusIcon />
                            {t('quantityOfferEditor.addTier')}
                          </Button>
                          {verticalLimitReached && (
                            <Text fontSize="caption" color="neutral-textLow">
                              {t('quantityOfferEditor.design.verticalLimitHelp', {
                                max: VERTICAL_MAX_TIERS,
                              })}
                            </Text>
                          )}
                        </Box>
                      </Box>

                      {/* Inline tier editor (image-8 fields). */}
                      {editingTier && (
                        <Box
                          marginTop="4"
                          padding="4"
                          borderColor="neutral-surfaceHighlight"
                          borderStyle="solid"
                          borderWidth="1"
                          borderRadius="2"
                          display="flex"
                          flexDirection="column"
                          gap="4"
                        >
                          {/* Title (with char counter) + Quantity on one row. */}
                          <Box display="flex" gap="3" alignItems="flex-start">
                            <Box flex="1" display="flex" flexDirection="column" gap="1">
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Label htmlFor="tier-title">
                                  {t('quantityOfferEditor.tier.title')}
                                </Label>
                                <Text fontSize="caption" color="neutral-textLow">
                                  {editingTier.title.length}/{TITLE_MAX}
                                </Text>
                              </Box>
                              <Input
                                id="tier-title"
                                value={editingTier.title}
                                maxLength={TITLE_MAX}
                                onChange={(e) =>
                                  updateTier(editingTier.id, {
                                    title: (e.target as HTMLInputElement).value,
                                  })
                                }
                              />
                              <Text fontSize="caption" color="neutral-textLow">
                                {t('quantityOfferEditor.tier.titleHelp')}
                              </Text>
                            </Box>
                            <Box width="140px">
                              <NumberControl
                                id="tier-quantity"
                                label={t('quantityOfferEditor.tier.quantity')}
                                value={editingTier.quantity}
                                min={1}
                                max={999}
                                onChange={(quantity) => updateTier(editingTier.id, { quantity })}
                              />
                            </Box>
                          </Box>

                          <CheckboxControl
                            name="tier-preselect"
                            label={t('quantityOfferEditor.tier.preselect')}
                            checked={editingTier.preselect}
                            onChange={(preselect) => setPreselect(editingTier.id, preselect)}
                          />

                          {/* The "Best deal" badge. Shown here, under the preselect checkbox,
                              because that is the offer it marks — but the values live on `design`,
                              not on the tier, so moving the preselection carries the badge with it
                              instead of orphaning its text and colours (see the schema note). It is
                              only reachable while THIS tier is the preselected one, which is also
                              the only tier that can display it. */}
                          {editingTier.preselect && (
                            <Box
                              display="flex"
                              flexDirection="column"
                              gap="3"
                              padding="3"
                              borderColor="neutral-surfaceHighlight"
                              borderStyle="solid"
                              borderWidth="1"
                              borderRadius="2"
                            >
                              <CheckboxControl
                                name="design-best-deal"
                                label={t('quantityOfferEditor.tier.bestDeal')}
                                checked={design.best_deal_show}
                                onChange={(best_deal_show) => updateDesign({ best_deal_show })}
                              />
                              {design.best_deal_show && (
                                <>
                                  <Box display="flex" flexDirection="column" gap="1">
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Label htmlFor="best-deal-text">
                                        {t('quantityOfferEditor.tier.bestDealText')}
                                      </Label>
                                      <Text fontSize="caption" color="neutral-textLow">
                                        {design.best_deal_text.length}/{BEST_DEAL_TEXT_MAX}
                                      </Text>
                                    </Box>
                                    <Input
                                      id="best-deal-text"
                                      value={design.best_deal_text}
                                      maxLength={BEST_DEAL_TEXT_MAX}
                                      onChange={(e) =>
                                        updateDesign({
                                          best_deal_text: (e.target as HTMLInputElement).value,
                                        })
                                      }
                                    />
                                  </Box>
                                  <Box display="flex" gap="3" alignItems="flex-start">
                                    <Box flex="1">
                                      <ColorControl
                                        id="best-deal-bg"
                                        label={t('quantityOfferEditor.tier.bestDealBgColor')}
                                        value={design.best_deal_bg_color}
                                        onChange={(best_deal_bg_color) =>
                                          updateDesign({ best_deal_bg_color })
                                        }
                                      />
                                    </Box>
                                    <Box flex="1">
                                      <ColorControl
                                        id="best-deal-text-color"
                                        label={t('quantityOfferEditor.tier.bestDealTextColor')}
                                        value={design.best_deal_text_color}
                                        onChange={(best_deal_text_color) =>
                                          updateDesign({ best_deal_text_color })
                                        }
                                      />
                                    </Box>
                                  </Box>
                                  <Box display="flex" gap="3" alignItems="flex-start">
                                    <Box flex="1">
                                      <SliderControl
                                        label={t('quantityOfferEditor.tier.bestDealRadius')}
                                        value={design.best_deal_border_radius}
                                        min={0}
                                        max={40}
                                        onChange={(best_deal_border_radius) =>
                                          updateDesign({ best_deal_border_radius })
                                        }
                                      />
                                    </Box>
                                    <Box flex="1">
                                      <SelectControl<ButtonAnimation>
                                        id="best-deal-animation"
                                        label={t('quantityOfferEditor.tier.bestDealAnimation')}
                                        value={design.best_deal_animation}
                                        options={buttonAnimation.options.map((a) => ({
                                          value: a,
                                          label: t(`designer.custom.anim_${a}`),
                                        }))}
                                        onChange={(best_deal_animation) =>
                                          updateDesign({ best_deal_animation })
                                        }
                                      />
                                    </Box>
                                  </Box>
                                </>
                              )}
                              <Text fontSize="caption" color="neutral-textLow">
                                {t('quantityOfferEditor.tier.bestDealHelp')}
                              </Text>
                            </Box>
                          )}

                          <CheckboxControl
                            name="tier-hide-compare"
                            label={t('quantityOfferEditor.tier.hideComparePrice')}
                            checked={editingTier.hide_compare_price}
                            onChange={(hide_compare_price) =>
                              updateTier(editingTier.id, { hide_compare_price })
                            }
                          />

                          {/* Discount type + value side by side. The value cell stays in the
                              grid (empty) when the type is "none" so the columns keep aligning. */}
                          <Box display="flex" gap="3" alignItems="flex-start">
                            <Box flex="1">
                              <SelectControl
                                id="tier-discount-type"
                                label={t('quantityOfferEditor.tier.discountType')}
                                value={editingTier.discount_type}
                                options={discountOptions}
                                onChange={(discount_type) =>
                                  updateTier(editingTier.id, { discount_type })
                                }
                              />
                            </Box>
                            <Box flex="1">
                              {editingTier.discount_type !== 'none' && (
                                <NumberControl
                                  id="tier-discount-value"
                                  label={t('quantityOfferEditor.tier.discountValue')}
                                  value={editingTier.discount_value}
                                  min={0}
                                  max={1_000_000}
                                  onChange={(discount_value) =>
                                    updateTier(editingTier.id, { discount_value })
                                  }
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Price color + Plaque text. */}
                          <Box display="flex" gap="3" alignItems="flex-start">
                            <Box flex="1">
                              <ColorControl
                                id="tier-price-color"
                                label={t('quantityOfferEditor.tier.priceColor')}
                                value={editingTier.price_color}
                                onChange={(price_color) =>
                                  updateTier(editingTier.id, { price_color })
                                }
                              />
                            </Box>
                            <Box flex="1">
                              <TextControl
                                id="tier-plaque-text"
                                label={t('quantityOfferEditor.tier.plaqueText')}
                                value={editingTier.plaque_text}
                                onChange={(plaque_text) =>
                                  updateTier(editingTier.id, { plaque_text })
                                }
                              />
                            </Box>
                          </Box>

                          {/* Plaque text color + Plaque background color. */}
                          <Box display="flex" gap="3" alignItems="flex-start">
                            <Box flex="1">
                              <ColorControl
                                id="tier-plaque-text-color"
                                label={t('quantityOfferEditor.tier.plaqueTextColor')}
                                value={editingTier.plaque_text_color}
                                onChange={(plaque_text_color) =>
                                  updateTier(editingTier.id, { plaque_text_color })
                                }
                              />
                            </Box>
                            <Box flex="1">
                              <ColorControl
                                id="tier-plaque-bg-color"
                                label={t('quantityOfferEditor.tier.plaqueBgColor')}
                                value={editingTier.plaque_bg_color}
                                onChange={(plaque_bg_color) =>
                                  updateTier(editingTier.id, { plaque_bg_color })
                                }
                              />
                            </Box>
                          </Box>

                          <TextControl
                            id="tier-image-url"
                            label={t('quantityOfferEditor.tier.imageUrl')}
                            value={editingTier.image_url}
                            onChange={(image_url) => updateTier(editingTier.id, { image_url })}
                          />
                        </Box>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Section 3: design + behaviour. */}
                  <Text fontWeight="bold">{t('quantityOfferEditor.design.heading')}</Text>
                  <Card>
                    <Card.Body>
                      <Box display="flex" flexDirection="column" gap="4">
                        <Text fontWeight="medium">
                          {t('quantityOfferEditor.design.selectMode')}
                        </Text>

                        {/* "Display above buy button" only applies in popup form mode — in
                            embedded mode the form is already inline, so hide the option
                            entirely when the store's form mode is embedded. */}
                        {config.mode === 'popup' && (
                          <CheckboxControl
                            name="design-display-above"
                            label={t('quantityOfferEditor.design.displayAboveBuyButton')}
                            checked={design.display_above_buy_button}
                            onChange={(display_above_buy_button) =>
                              updateDesign({ display_above_buy_button })
                            }
                          />
                        )}

                        {/* Template chooser: three selectable preview cards. */}
                        <Box display="flex" gap="3">
                          {templates.map((tpl) => {
                            const active = design.template === tpl.value;
                            // Vertical can only draw VERTICAL_MAX_TIERS cards, so beyond that it is
                            // not a choice — no handler at all, rather than a click that silently
                            // drops the merchant's extra offers.
                            const unavailable = tpl.value === 'vertical' && verticalUnavailable;
                            return (
                              <Box
                                key={tpl.value}
                                flex="1"
                                position="relative"
                                display="flex"
                                flexDirection="column"
                                gap="3"
                                padding="3"
                                cursor={unavailable ? 'not-allowed' : 'pointer'}
                                borderStyle="solid"
                                borderWidth="1"
                                borderColor={
                                  active ? 'primary-interactive' : 'neutral-surfaceHighlight'
                                }
                                borderRadius="2"
                                backgroundColor={active ? 'primary-surface' : 'neutral-background'}
                                // Greyed out AND desaturated: the mock is pure colour, so opacity
                                // alone still left it looking like a live option.
                                //
                                // 🚨 Through `className`, NOT `style`. Nimbus's Box destructures
                                // the incoming `style` away and renders only the one its own
                                // sprinkle props produce, so an inline style here is silently
                                // dropped — which is exactly what happened: the card was
                                // unclickable but still looked like a live option. `className` IS
                                // merged, so the rule lives in `index.css`.
                                className={unavailable ? 'cod-template-disabled' : undefined}
                                aria-disabled={unavailable || undefined}
                                onClick={unavailable ? undefined : () => selectTemplate(tpl.value)}
                              >
                                {active && (
                                  <Box
                                    position="absolute"
                                    top="-8px"
                                    right="-8px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    width="20px"
                                    height="20px"
                                    borderRadius="full"
                                    backgroundColor="primary-interactive"
                                    color="neutral-background"
                                  >
                                    <CheckIcon size="small" />
                                  </Box>
                                )}
                                <Text fontWeight="bold" fontSize="caption">
                                  {tpl.label}
                                </Text>
                                <TemplateMock template={tpl.value} />
                              </Box>
                            );
                          })}
                        </Box>

                        {/* The vertical layout can only fit a few readable cards, so make the limit
                            explicit whenever it's the selected template — and, once it has been
                            greyed out, as the reason it can no longer be picked.

                            Two notes, not one string with a tint swapped: the blue one states a
                            cap the merchant is still inside, while the amber one has to explain a
                            control that just went dead — which offers there are too many of, that
                            buyers would never get to choose the extras, and what to do about it.

                            Two tints, same note: informational blue while vertical is merely
                            selected, amber once it is the reason a template just went dead. As a
                            plain grey caption under three colourful cards it read as boilerplate
                            and merchants scrolled past the one sentence explaining the grey-out.
                            Matches the inline notice in Settings rather than an `Alert`, which
                            wants a title and a full-width block for what is one sentence. */}
                        {(verticalOfferLimit || verticalUnavailable) && (
                          <Box
                            display="flex"
                            gap="2"
                            padding="3"
                            borderRadius="2"
                            backgroundColor={
                              verticalUnavailable ? 'warning-surface' : 'primary-surface'
                            }
                          >
                            <span
                              style={{
                                color: verticalUnavailable ? '#9a6b00' : '#2e5aac',
                                display: 'flex',
                                flex: '0 0 auto',
                                marginTop: 1,
                              }}
                            >
                              <InfoCircleIcon size="small" />
                            </span>
                            <Box flex="1">
                              <Text
                                fontSize="caption"
                                color={
                                  verticalUnavailable ? 'warning-textHigh' : 'primary-textHigh'
                                }
                              >
                                {verticalUnavailable
                                  ? t('quantityOfferEditor.design.verticalUnavailableHelp', {
                                      max: VERTICAL_MAX_TIERS,
                                      offers: draft.tiers.length,
                                    })
                                  : t('quantityOfferEditor.design.verticalLimitHelp', {
                                      max: VERTICAL_MAX_TIERS,
                                    })}
                              </Text>
                            </Box>
                          </Box>
                        )}

                        {/* Palette swatches: horizontally scrollable, each split into a soft
                            (left) and hard (right) half. */}
                        <Box display="flex" flexDirection="column" gap="1">
                          <Text>{t('quantityOfferEditor.design.palette')}</Text>
                          {/* The strip spans the card at every viewport, showing five swatches and
                              scrolling the rest — the swatches share the width rather than the
                              strip stopping at a fixed one, which left a gutter on wider cards. A
                              plain div is used so the overflow styles actually apply;
                              `cod-palette-strip` carries the width, the containment that keeps the
                              swatches from widening the page, and a permanently visible scrollbar
                              (see `index.css`). */}
                          <div
                            className="cod-palette-strip"
                            style={{
                              display: 'flex',
                              gap: PALETTE_GAP,
                              overflowX: 'auto',
                              paddingBottom: 4,
                            }}
                          >
                            {PALETTES.map((p) => (
                              <div
                                key={p.id}
                                onClick={() =>
                                  updateDesign({
                                    palette: p.id,
                                    bg_color: p.bg,
                                    border_color: p.border,
                                  })
                                }
                                style={{
                                  // A fifth of the strip, minus the four 8px gaps between the five
                                  // — so exactly PALETTES_PER_VIEW show whatever width the card
                                  // has, and the rest scroll. `border-box` because the swatch
                                  // carries its own padding and border.
                                  flex: `0 0 calc((100% - ${(PALETTES_PER_VIEW - 1) * PALETTE_GAP}px) / ${PALETTES_PER_VIEW})`,
                                  boxSizing: 'border-box',
                                  cursor: 'pointer',
                                  padding: 4,
                                  borderStyle: 'solid',
                                  borderWidth: 1,
                                  borderColor:
                                    design.palette === p.id ? '#2563eb' : 'rgba(0,0,0,0.1)',
                                  borderRadius: 8,
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    width: '100%',
                                    height: 28,
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                  }}
                                >
                                  <div style={{ flex: 1, background: p.bg }} />
                                  <div style={{ flex: 1, background: p.border }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </Box>

                        {/* Background + border color side by side. */}
                        {/* Wraps below `md`: two colour controls sharing a phone card left each
                            one ~114px, and a colour control cannot usefully be narrower than its
                            swatch plus a readable hex. Stacked, both get the full width. */}
                        <Box
                          display="flex"
                          gap="3"
                          alignItems="flex-start"
                          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
                        >
                          <Box flex={{ xs: '1 1 100%', md: '1' }} minWidth="0">
                            <ColorControl
                              id="design-bg-color"
                              label={t('quantityOfferEditor.design.bgColor')}
                              value={design.bg_color}
                              onChange={(bg_color) => updateDesign({ bg_color })}
                            />
                          </Box>
                          <Box flex={{ xs: '1 1 100%', md: '1' }} minWidth="0">
                            <ColorControl
                              id="design-border-color"
                              label={t('quantityOfferEditor.design.borderColor')}
                              value={design.border_color}
                              onChange={(border_color) => updateDesign({ border_color })}
                            />
                          </Box>
                        </Box>

                        <SliderControl
                          label={t('quantityOfferEditor.design.pricesSize')}
                          value={design.prices_size}
                          min={10}
                          max={30}
                          hideValue
                          onChange={(prices_size) => updateDesign({ prices_size })}
                        />
                        <SliderControl
                          label={t('quantityOfferEditor.design.borderRadius')}
                          value={design.border_radius}
                          min={0}
                          max={40}
                          hideValue
                          onChange={(border_radius) => updateDesign({ border_radius })}
                        />

                        <CheckboxControl
                          name="design-hide-image"
                          label={t('quantityOfferEditor.design.hideProductImage')}
                          checked={design.hide_product_image}
                          onChange={(hide_product_image) => updateDesign({ hide_product_image })}
                        />
                        <CheckboxControl
                          name="design-hide-variant-numbers"
                          label={t('quantityOfferEditor.design.hideVariantNumbers')}
                          checked={design.hide_variant_numbers}
                          onChange={(hide_variant_numbers) =>
                            updateDesign({ hide_variant_numbers })
                          }
                        />

                        <Box marginY="2" height="1px" backgroundColor="neutral-surfaceHighlight" />
                        <Box display="flex" flexDirection="column" gap="4">
                          <CheckboxControl
                            name="design-disable-variant-selectors"
                            label={t('quantityOfferEditor.design.disableVariantSelectors')}
                            checked={design.disable_variant_selectors}
                            onChange={(disable_variant_selectors) =>
                              updateDesign({ disable_variant_selectors })
                            }
                          />
                          {/* 🚨 No "use always the first variant" checkbox either (retired
                              2026-08-20). With the pickers off, every unit follows the variant the
                              buyer picked on the product page — the only behaviour the legacy
                              renderer ever had, and the only one that ships a bundle of what the
                              buyer was looking at. See `use_first_variant` in `config-schema`. */}
                          {/* 🚨 No "disable when the page quantity exceeds the offer" checkbox.
                              Nuvemshop does not expose the quantity a buyer picked on the product
                              page — it is only observable once the item reaches the cart — so the
                              guard's input is ALWAYS 1 (`INITIAL_QUANTITY` on the legacy path, a
                              literal `1` at `main.ts:197`) and the setting can never fire. It was a
                              switch that promised something the platform cannot tell us. The schema
                              field stays so existing configs keep validating; see
                              `disable_when_qty_exceeds` in `config-schema`. */}
                        </Box>
                      </Box>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Box>
          </Layout.Section>

          <Layout.Section minWidth="0">
            <div style={{ position: 'sticky', top: 16 }}>
              <Card>
                <Card.Header title={t('designer.preview')} />
                <Card.Body>
                  {draft.products.length === 0 ? (
                    <Box display="flex" justifyContent="center" paddingY="6">
                      <Text fontWeight="bold" color="neutral-textLow">
                        {t('quantityOfferEditor.selectProductPlaceholder')}
                      </Text>
                    </Box>
                  ) : (
                    <Box backgroundColor="neutral-surface" padding="4" borderRadius="2">
                      <LivePreview
                        config={config}
                        previewOffer={draft}
                        previewVariants={
                          draft.products.find((p) => p.variants.length > 0)?.variants
                        }
                        previewPrice={draft.products.find((p) => p.price != null)?.price}
                        previewImage={draft.products.find((p) => p.image)?.image}
                        offersOnly
                      />
                    </Box>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Layout.Section>
        </Layout>
      </Page.Body>

      <ProductPicker
        open={pickerOpen}
        initialSelected={draft.products}
        onAdd={addProducts}
        onClose={() => setPickerOpen(false)}
      />
    </Page>
  );
}
